import type { ReactNode } from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import styles from './ModelPill.module.css';
import type { ChatTheme, ModelVendor } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The mark is per-VENDOR, never per-model.
 *
 * *Hover model* and *Marketing lead* draw the same pill with the same geometry and two different
 * marks — "Opus 5" behind the Claude asterisk, "Gemini 3.1 Pro" behind the Google G. Keying the
 * asset off
 * the label string ("does it start with Opus?") would have to be edited every time a model ships;
 * keying it off the vendor never does. Both marks are brand assets and both keep their own hexes.
 */
const VENDOR_MARK: Record<ModelVendor, string> = {
  /** viewBox 0 0 20 20, a single path at `#D97757`. */
  claude: 'claude-logo',
  /** viewBox 0 0 24 24, four brand hexes — drawn at the same 20px box as the Claude mark. */
  google: 'google-mark',
};

export interface ModelPillProps {
  /** Picks the 20px brand mark. The label beside it is free text, so this is the only closed part. */
  vendor: ModelVendor;
  /**
   * Draws this instead of the vendor mark, at the same 20px.
   *
   * The union above is the two vendors the artboards draw, and it is deliberately closed — a
   * `string` there would put icon-registry lookups back in every caller. But the dashboard's model
   * list is the product's full one (GPT, Grok, DeepSeek, Qwen, Kimi…), and a pill that answered
   * "GPT-5.5" with the Claude asterisk is a worse component than one with an escape hatch. Hosts
   * that hold a mark of their own hand it over; everyone else keeps passing `vendor` and nothing
   * about this pill changes.
   *
   * `vendor` stays required, as the fallback and as the thing that is true in the design.
   */
  mark?: ReactNode;
  /** Free string — "Opus 5", "Haiku 4.5", "Gemini 3.1 Pro". Never truncates. */
  label: string;
  /**
   * Extended thinking is on for this model — draws a small brain between the name and the chevron.
   *
   * It moved here from the model list's rows (Beka, 2026-09-05). In the list it was redundant, and
   * worse than redundant: the two rows it distinguished rendered under the same name, so the badge
   * was carrying a difference the names should have carried. On the PILL it is the only thing that
   * says so — the name is the same either way, and the switch that set it is two menus deep.
   *
   * Before the chevron, not after the mark: the mark is the model's identity and the brain is a
   * setting on it, so it belongs with the chrome at the trailing edge rather than inside the badge
   * that names the vendor.
   */
  thinking?: boolean;
  /**
   * The model picker is open beneath it. Paints the same light-6 wash *and* the same one-step tone
   * lift hover does, and holds both for as long as the picker is up — the pointer has moved into
   * the popover, and the chip it came from is still the thing that is open.
   */
  open?: boolean;
  onClick?: () => void;
  theme?: ChatTheme;
  /** Stories and visual tests only: paints the hover wash and tone lift without a real pointer. */
  forceHover?: boolean;
  className?: string;
}

/**
 * The composer's model chip: brand mark, model name, chevron — a full-round 32px pill that paints
 * no ground until you touch it.
 *
 * Deliberately **not** the same component as `ModePill`, even though both are 32px full-round pills
 * with a glyph and a label. This one is 8/10 padding, gap 7, a 20px mark and a subtle label; that
 * one is 9/11, gap 6, an 18px globe and a blue label. A `variant` prop would average those six
 * numbers into a lie, and the pills would drift apart the first time either side is retouched.
 *
 * Hover does two things, not one: the light-6 wash arrives **and** the label and chevron each step
 * up one tone (subtle -> default, hint -> muted). `open` holds both.
 *
 * That is a deliberate departure from the file, and this component is where the discrepancy was
 * found. *Hover add* washes and lifts its glyph muted -> subtle; *Hover model* washes and lifts
 * nothing — two controls 4px apart in the same bar answering the same pointer differently. Beka
 * ruled to unify on the brighter reading (DECISIONS §3), which is also what
 * `v1_5/Header/IconSlot` already does, so the composer and the bar above it now behave alike.
 * The lift itself lives in `ModelPill.module.css`, which carries the per-tone reasoning.
 */
export function ModelPill({
  vendor,
  mark,
  label,
  thinking = false,
  open = false,
  onClick,
  theme: themeProp,
  forceHover = false,
  className,
}: ModelPillProps) {
  const theme = useV15Theme(themeProp);
  return (
    <button
      type="button"
      className={cn(
        chatThemeClass(theme),
        styles.pill,
        open && styles.open,
        forceHover && styles.forceHover,
        className,
      )}
      onClick={onClick}
      // The pill's whole job is to open the model picker, so it announces itself as a menu button.
      // Its accessible name is the visible label, which is why there is no `aria-label` here.
      aria-haspopup="menu"
      aria-expanded={open}
    >
      <span className={styles.mark}>
        {/* `preserveColors` on both: these are brand marks, and DeckIcon would otherwise rewrite
            every hex to `currentColor` and flatten the Google G's four colours into one. */}
        {mark ?? <DeckIcon name={VENDOR_MARK[vendor]} size={20} preserveColors />}
      </span>
      <span className={styles.label}>{label}</span>
      {thinking ? (
        // `title` and not `aria-label`: the pill's accessible name is already the model, and a
        // second name on a child would have a reader announce the button twice. The state is
        // reachable in the picker this pill opens, which is where it can also be changed.
        <span className={styles.thinking} title="Extended thinking">
          <DeckIcon name="brain" size={14} />
        </span>
      ) : null}
      {/* The mark above stays a raw `DeckIcon` — it is brand ink and its own identity. This one is
          chrome: it says the pill opens a picker, which is a fact about the button. `inherit`
          because the tone is three states deep — hint at rest, muted on hover, and muted again
          while the picker is open — and all three live in one place in the stylesheet. */}
      <span className={styles.chevron}>
        <ActivityGlyph chrome="chevron-down" size={16} tone="inherit" theme={theme} />
      </span>
    </button>
  );
}
